/* eslint-disable @typescript-eslint/no-explicit-any */
import { AddToCartType, CouponTarget, Languages, VoucherTypes } from '@/constants'
import { useAuth, useCart, useStore } from '@/containers'
import { CouponArrowDown, CouponIconSimple, IconClose } from '@/styles/Icon'
import type { Cart, VoucherItem } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { convertCoupon } from '@/utils/next-util'
import { Dialog, Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { getCookie } from 'cookies-next'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import type { SetStateAction } from 'react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ImageOptimize from '../ImageOptimize'
import FormRadioButtonItems from './FormRadioButtonItems'

interface FormDiscount {
  className?: string
  onClick?: () => void
  icon?: boolean
  disabled?: boolean
  isError?: boolean | string
  type?: 'button' | 'submit' | 'reset' | undefined
  typeButtonPrimary?: 'button' | 'submit' | 'reset' | undefined
  classButtonConfirm?: string
  id?: string
  name?: string
  value?: string
  primary?: boolean
  secondary?: boolean
  // onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder: string
  buttonPlaceholder: string
  buttonText: string
  shippingMethod?: string
  discount?: number
}

const FormDiscount = ({
  name = '',
  id = '',
  shippingMethod,
  placeholder = 'Mã giảm giá',
  buttonText = 'Áp dụng',
  discount = 0
}: FormDiscount) => {
  const { selectedLang } = useStore()
  const { getCartCoupons, cartCoupons, setCouponApply, setIsPreOrder, couponApply } = useCart()
  const { fetchCart, setErrorAuth, errorAuth, cart, voucherApply, setVoucherApply } = useAuth()
  const [couponSelected, setCouponSelected] = useState<string[]>(
    (voucherApply.length > 0 && voucherApply) || couponApply || []
  )

  const [inputVoucher, setInputVoucher] = useState('')
  const [error, setError] = useState('')
  const [showListCoupons, setShowListCoupons] = useState(false)
  const [isApply, setIsApply] = useState(false)
  const t = useTranslations('Cart')
  const router = useRouter()
  useEffect(() => {
    getCartCoupons()
    setInputVoucher('')
    setIsApply(false)
    setErrorAuth({
      status: false,
      message: '',
      key: ''
    })
  }, [])

  useEffect(() => {
    if (router?.query?.type == 'preOrder') {
      setIsPreOrder(true)
    }
  }, [router])

  useEffect(() => {
    if (errorAuth?.status) {
      setError(errorAuth.message)
    }
  }, [errorAuth])
  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'USD'
      default:
        return 'VND'
    }
  }
  const handleChange = (event: { target: { value: SetStateAction<string> } }) => {
    setInputVoucher(event.target.value)
    setCouponApply([event.target.value as string])
    if (event.target.value) {
      setIsApply(true)
    }
  }

  const clearCoupon = () => {
    setCouponApply([])
    setInputVoucher('')
    setVoucherApply([])
    fetchCart({
      shippingMethod,
      ...(router?.query?.type === AddToCartType.PRE_ORDER &&
        router?.query?.id && {
          productIds: router?.query?.id as string | undefined
        }),
      ...(router?.query?.type === AddToCartType.INSTALLMENT &&
        router?.query?.id && {
          productIds: (router?.query?.id + ':1') as string | undefined
        }),
      action: 'form discount 2'
    })
    setIsApply(false)
    setErrorAuth({
      status: false,
      message: '',
      key: ''
    })
  }
  function checkVoucherType(voucher: VoucherItem) {
    switch (voucher.type) {
      case VoucherTypes.PERCENT:
        return voucher.percentValue + '%'
      case VoucherTypes.FIXED:
        return voucher.fixedValue
          .toLocaleString(selectedLang as string, {
            style: 'currency',
            currency: currency(selectedLang as string)
          })
          .replace(/\s₫/g, 'đ')
      default:
        break
    }
  }
  const applyCoupon = async () => {
    setShowListCoupons(false)
    setVoucherApply([])
    setCouponApply(couponSelected)
    await updateVoucher(couponSelected)
  }

  function updateVoucher(code: string[], manually = false) {
    fetchCart({
      coupon: code,
      shippingMethod,
      action: 'form discount 3'
    })
      .then((res: Cart) => {
        setErrorAuth({
          status: false,
          message: '',
          key: ''
        })
        if (res.coupons.filter(e => e.mes).length > 0) {
          if (manually && res.coupons.length > 1) {
            setShowListCoupons(false)
            setInputVoucher('')
          }
          const couponApplied = res.coupons.filter(e => !e.mes).map(e => e.coupon.initialKey) as string[]
          setCouponSelected(couponApplied)
          setCouponApply(couponApplied)
        }
        setShowListCoupons(false)
        getCartCoupons()
        toast.success(
          getCookie('language') === Languages.EN
            ? 'Apply promotional code successfully!'
            : 'Áp dụng mã khuyến mại thành công!',
          {
            position: 'bottom-center',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            progress: undefined,
            theme: 'light'
          }
        )
        setError('')
      })
      .catch(err => {
        setError(err.message)
        setErrorAuth({
          status: true,
          message: err.message,
          key: 'FormDiscount'
        })
        fetchCart({
          coupon: []
        })
      })
  }

  const cardCouponsMapping = useMemo(() => {
    if (!cartCoupons?.length) return []
    return cartCoupons.reduce((acc: any, cur: VoucherItem) => {
      if (cur.target === CouponTarget.PRODUCT) {
        const productGroup = acc.find((e: any) => e._id === cur.product?._id)
        const couponApply = cart?.coupons?.find(
          coupon => coupon.coupon?.initialKey === convertCoupon(cur.code, cur.product?._id)
        )
        if (productGroup) {
          productGroup.coupons.push({
            ...cur,
            ...(couponApply && {
              mes: couponApply.mes
            })
          })
          return acc
        }
        return [
          ...acc,
          {
            ...cur.product,
            coupons: [
              {
                ...cur,
                ...(couponApply && {
                  mes: couponApply.mes
                })
              }
            ]
          }
        ]
      }

      const couponApply = cart?.coupons?.find(coupon => coupon.coupon?.initialKey === `${cur.code}_orderCoupon`)
      return [
        ...acc,
        {
          name: {
            vi: 'Áp dụng cho đơn hàng'
          },
          _id: 'orderCoupon',
          coupons: [
            {
              ...cur,
              ...(couponApply && {
                mes: couponApply.mes
              })
            }
          ]
        }
      ]
    }, [] as any)
  }, [cartCoupons, cart])

  const couponOnchange = (value: string[]) => {
    const couponErrors = cart.coupons?.filter(e => e.mes)?.map(e => e.coupon?.initialKey)
    const changed =
      value.find(item => !couponSelected.includes(item)) || couponSelected.find(item => !value.includes(item))
    const data = value
      .map(e => {
        const [couponCode, productId] = changed?.split('_') as any[]
        if (!e.includes(couponCode) && e.includes(productId)) {
          return null
        }
        return e
      })
      .filter(Boolean)
      .filter(e => {
        if (couponErrors?.length > 0) {
          return !couponErrors.includes(e as string)
        }
        return true
      })
    setCouponSelected(data as string[])
  }

  return (
    <div className='discountBox bg-grey-200 rounded py-2' id='voucher'>
      <div
        className=' relative mb-[10px] flex h-[48px] w-full cursor-pointer flex-row items-center border border-white-500 bg-white'
        onClick={() => setShowListCoupons(true)}
      >
        <span className='icon mx-[15px]'>
          <CouponIconSimple />
        </span>
        <span className='text-Headline leading-none tracking-tight'>Sử dụng mã giảm giá</span>
        <CouponArrowDown className='absolute right-[15px] top-[35%]' />
      </div>
      {cart?.coupons?.filter(e => e.mes).length > 0 && (
        <div className='mb-1 w-full text-base font-normal italic leading-tight text-primary-base opacity-80'>
          Một trong số các voucher không hợp lệ, tiếp tục mua hàng mà không cần voucher đó
        </div>
      )}
      {discount && discount > 0 ? (
        <div className='mb-1 text-dark-400'>
          Đã áp dụng <strong>{cart.coupons.filter(e => !e.mes).length}</strong> mã (giảm{' '}
          <strong className='font-bold text-primary-base'>
            {discount
              ?.toLocaleString(selectedLang as string, {
                style: 'currency',
                currency: currency(selectedLang as string)
              })
              .replace(/\s₫/g, 'đ')}
          </strong>
          )
        </div>
      ) : (
        ''
      )}
      <div className='w-full overflow-y-auto'>
        <FormRadioButtonItems className='flex flex-wrap !gap-2 '>
          {!errorAuth?.status &&
            cart?.coupons &&
            cart.coupons
              .filter(e => !e.mes)
              .map((e, i) => {
                return (
                  <div
                    key={i}
                    className={`voucherItem flex h-[38px] w-full max-w-[135px] justify-center  bg-[url('/assets/icons/couponBg.svg')] bg-cover bg-no-repeat`}
                  >
                    <span className='text-Voucher self-center text-primary-base'>
                      Giảm{' '}
                      {e.value
                        .toLocaleString(Languages.VN, {
                          style: 'currency',
                          currency: 'VND'
                        })
                        .replace(/\s₫/g, 'đ')}
                    </span>
                  </div>
                )
              })}
        </FormRadioButtonItems>
      </div>
      <Transition appear show={showListCoupons} as={Fragment}>
        <Dialog as='div' className={`relative z-[9999]`} onClose={() => applyCoupon()}>
          <Transition.Child
            as={Fragment}
            enter='ease-in-out duration-500'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in-out duration-500'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bottom-0 left-0 bg-black-150 transition-opacity' />
          </Transition.Child>
          <div className='pointer-events-none fixed bottom-0 left-[50%] z-50 flex h-full max-h-[60vh] w-full max-w-[440px] translate-x-[-50%] overflow-hidden rounded-tl-sm rounded-tr-sm '>
            <Transition.Child
              as={Fragment}
              enter='transform transition ease-in-out duration-500'
              enterFrom='translate-y-[70%]'
              enterTo='translate-y-[0%]'
              leave='transform transition ease-in-out duration-500'
              leaveFrom='translate-y-[0%]'
              leaveTo='translate-y-[100%]'
            >
              <Dialog.Panel className='pointer-events-auto h-full w-full max-w-[440px] shadow-modal'>
                <div className={`block w-full transform overflow-hidden bg-white pb-6 align-middle shadow-modal`}>
                  <div className={`headerModal relative h-[60px] w-full px-3 pt-4`}>
                    <Dialog.Title as='h2' className={`mt-1 text-titleSection`}>
                      {t('voucherForYou')}
                    </Dialog.Title>

                    <button
                      onClick={() => setShowListCoupons(false)}
                      className='absolute right-3 top-4 z-10 cursor-pointer'
                    >
                      <IconClose />
                    </button>
                  </div>
                  <div className='mb-2 flex items-center justify-between gap-2 px-3'>
                    <div className='inputGroup relative flex-1'>
                      <input
                        placeholder={placeholder}
                        type='text'
                        name={name}
                        id={id}
                        value={inputVoucher}
                        onChange={handleChange}
                        className={clsx(
                          error && 'border-red',
                          'border-grey text-fieldInput focus:border-grey h-[48px] w-full rounded-lg border p-3 text-base font-bold text-blue shadow-none placeholder:font-normal placeholder:text-dark-400/70 focus:shadow-none focus:outline-none focus:ring-0'
                        )}
                      />
                      {inputVoucher ? (
                        <button
                          className='clear absolute right-3 top-[50%] z-10 translate-y-[-50%]'
                          type='button'
                          onClick={() => clearCoupon()}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width={20}
                            height={20}
                            viewBox='0 0 20 20'
                            fill='none'
                          >
                            <circle cx={10} cy={10} r='9.5' fill='#B1B1B1' />
                            <path
                              d='M14.0716 12.7695C14.4319 13.1299 14.4319 13.7123 14.0716 14.0728C13.8919 14.2525 13.656 14.3428 13.4199 14.3428C13.184 14.3428 12.9481 14.2525 12.7683 14.0728L9.99902 11.3032L7.2297 14.0728C7.04998 14.2525 6.81407 14.3428 6.57816 14.3428C6.34208 14.3428 6.10617 14.2525 5.92646 14.0728C5.56618 13.7123 5.56618 13.1299 5.92646 12.7695L8.69595 10.0001L5.92646 7.23071C5.56618 6.87026 5.56618 6.28789 5.92646 5.92744C6.2869 5.56716 6.86925 5.56716 7.2297 5.92744L9.99902 8.697L12.7683 5.92744C13.1288 5.56716 13.7111 5.56716 14.0716 5.92744C14.4319 6.28789 14.4319 6.87026 14.0716 7.23071L11.3021 10.0001L14.0716 12.7695Z'
                              fill='white'
                            />
                          </svg>
                        </button>
                      ) : (
                        ''
                      )}
                    </div>
                    <button
                      className='bg-primary-basepx-[10px] h-[48px] max-w-[110px] flex-[110px] rounded-lg py-0 font-bold uppercase text-white disabled:bg-gray-1000 disabled:outline-none'
                      disabled={!isApply}
                      type='button'
                      onClick={() => updateVoucher([inputVoucher], true)}
                    >
                      {buttonText}
                    </button>
                  </div>
                  {inputVoucher && errorAuth && (
                    <div className='mb-1 ml-3 w-full text-mainBody font-normal leading-tight text-primary-base opacity-70'>
                      {errorAuth.message}
                    </div>
                  )}
                  <p className='h-[60px] px-3 text-base font-normal text-neutral-600 '>{t('voucherSubTitle')}</p>
                  <div
                    className={`scrollBarCS mb-5 mr-2 h-full max-h-[calc(60vh-144px)] min-h-[calc(60vh-144px)] max-w-[calc(100%-8px)] pl-3 pr-2`}
                  >
                    {cardCouponsMapping && cardCouponsMapping.length > 0 ? (
                      <Listbox value={couponSelected} onChange={async (e: string[]) => couponOnchange(e)} multiple>
                        <Listbox.Options
                          as='div'
                          static
                          className={`flex w-full cursor-pointer flex-wrap items-start justify-start overflow-auto rounded-md bg-white text-base`}
                        >
                          <div>
                            {cardCouponsMapping.map((e: any) => (
                              <div key={e._id}>
                                <p className={clsx(`text-Headline mb-3 text-dark-500`)}>
                                  {e._id === 'orderCoupon' ? (
                                    <strong>Áp dụng cho đơn hàng</strong>
                                  ) : (
                                    <>
                                      <strong>Sản phẩm áp dụng: </strong> {getTranslate(e.name)}
                                    </>
                                  )}
                                </p>
                                <div className='ml-[40px] flex flex-wrap gap-2 pb-2'>
                                  {e.coupons &&
                                    e.coupons.map((c: VoucherItem) => (
                                      <Listbox.Option
                                        as='div'
                                        key={c._id}
                                        className={({ selected }) =>
                                          clsx(
                                            `middle text-fieldInput relative mr-1 w-full cursor-pointer before:absolute before:left-[-40px] before:top-1/2 before:-translate-y-1/2 before:transform`,
                                            c.mes
                                              ? `!text-yellow-500 before:content-[url('/assets/icons/couponWarning.svg')]`
                                              : selected
                                              ? `before:content-[url('/assets/icons/couponChecked.svg')]`
                                              : `before:content-[url('/assets/icons/couponPending.svg')]`
                                          )
                                        }
                                        value={`${c.code}_${e._id}`}
                                      >
                                        <div
                                          className={clsx(
                                            'voucherItem flex h-20 w-full items-center overflow-hidden rounded-lg bg-red-600 bg-opacity-5',
                                            c.mes && '!bg-yellow-500/10'
                                          )}
                                        >
                                          <div className='voucherLeft min-w-[120px] max-w-[30%] flex-[30%]'>
                                            <div className='flex w-full flex-wrap justify-center'>
                                              <p
                                                className={clsx(
                                                  'w-full text-center text-base text-primary-base',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                Giảm
                                              </p>
                                              <p
                                                className={clsx(
                                                  'w-full text-center text-xl font-extrabold text-primary-base',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {checkVoucherType(c)}
                                              </p>
                                              {c.percentValue && c.fixedValue && (
                                                <i
                                                  className={clsx(
                                                    'text-Voucher text-primary-base',
                                                    c.mes && '!text-yellow-500'
                                                  )}
                                                >
                                                  (~
                                                  {c.fixedValue
                                                    .toLocaleString(Languages.VN, {
                                                      style: 'currency',
                                                      currency: 'VND'
                                                    })
                                                    .replace(/\s₫/g, 'đ')}
                                                  )
                                                </i>
                                              )}
                                            </div>
                                          </div>
                                          <div className='voucherRight max-w-[70%] flex-[70%]'>
                                            <div className='flex w-full flex-wrap items-center pl-3 leading-4'>
                                              <p
                                                className={clsx(
                                                  'mb-1.5 w-full text-sm font-bold text-primary-base',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {dayjs(c.startTime).format('HH:mm DD.MM')} ~{' '}
                                                {dayjs(c.endTime).format('HH:mm DD.MM')}
                                              </p>
                                              <div
                                                className={clsx(
                                                  'mb-0 w-full text-xs font-normal text-primary-base',
                                                  c.mes && '!text-yellow-500'
                                                )}
                                              >
                                                {getTranslate(c.description)}
                                              </div>
                                              {c.minValue ? (
                                                <div
                                                  className={clsx(
                                                    'flex text-xs font-normal text-primary-base',
                                                    c.mes && '!text-yellow-500'
                                                  )}
                                                >
                                                  <span>{t('minValueLabel')}</span>{' '}
                                                  <span className='ml-[4px] font-bold'>
                                                    {c.minValue
                                                      .toLocaleString(selectedLang as string, {
                                                        style: 'currency',
                                                        currency: currency(selectedLang as string)
                                                      })
                                                      .replace(/\s₫/g, 'đ')}
                                                  </span>
                                                </div>
                                              ) : (
                                                ''
                                              )}
                                              {c.maxDiscountValue && c.type !== VoucherTypes.FIXED && (
                                                <div
                                                  className={clsx(
                                                    'flex text-xs font-normal text-primary-base',
                                                    c.mes && '!text-yellow-500'
                                                  )}
                                                >
                                                  <span>{t('maxDiscountValue')}</span>{' '}
                                                  <span className='ml-[4px] font-bold'>
                                                    {c.maxDiscountValue
                                                      .toLocaleString(selectedLang as string, {
                                                        style: 'currency',
                                                        currency: currency(selectedLang as string)
                                                      })
                                                      .replace(/\s₫/g, 'đ')}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        {c.mes && <span>{c.mes}</span>}
                                      </Listbox.Option>
                                    ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </Listbox.Options>
                      </Listbox>
                    ) : (
                      <div className='emptyVoucher flex w-full flex-wrap justify-center'>
                        <ImageOptimize src={'../empty-image.png'} width={240} height={240} alt='Empty' />
                        <div className='w-full text-center text-base font-normal leading-7 text-neutral-600'>
                          Chưa có mã giảm giá phù hợp
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default FormDiscount
